import buildManifest from "../generated/app-version.manifest.json";

export type AppVersionInfo = {
  version: string;
  environment: string;
  buildTime: string;
  gitCommit: string;
};

declare global {
  interface Window {
    __MYTARA_APP_INFO__?: AppVersionInfo;
  }
}

const VERSION_ENV_SUFFIX =
  /-(dev|qa|local|staging|stage|preprod|prod|production|test)$/i;

const readEnv = (key: string, fallback: string): string => {
  const value = process.env[key];
  return typeof value === "string" && value.length > 0 ? value : fallback;
};

const manifestVersion =
  typeof buildManifest?.version === "string" ? buildManifest.version : "0.1.0";
const manifestEnvironment =
  typeof buildManifest?.environment === "string" ? buildManifest.environment : "local";
const manifestBuildTime =
  typeof buildManifest?.buildTime === "string" ? buildManifest.buildTime : "";
const manifestGitCommit =
  typeof buildManifest?.gitCommit === "string" ? buildManifest.gitCommit : "unknown";

/** Semantic version from package.json (injected at build time). */
export const APP_VERSION = readEnv("REACT_APP_VERSION", manifestVersion);

/** Deployment environment label (dev, qa, preprod, production, …). */
export const APP_ENVIRONMENT = readEnv("REACT_APP_ENV", manifestEnvironment);

/** ISO timestamp when the bundle was built. */
export const APP_BUILD_TIME = readEnv("REACT_APP_BUILD_TIME", manifestBuildTime);

/** Short git commit hash baked into the build, when available. */
export const APP_GIT_COMMIT = readEnv("REACT_APP_GIT_COMMIT", manifestGitCommit);

/** User-facing version without environment suffixes (e.g. `0.1.0-qa` → `0.1.0`). */
export function stripVersionEnvSuffix(version: string): string {
  return version.replace(VERSION_ENV_SUFFIX, "");
}

export function getDisplayVersion(version = APP_VERSION): string {
  return stripVersionEnvSuffix(version);
}

export function getAppVersionInfo(): AppVersionInfo {
  const runtime = globalThis.window?.__MYTARA_APP_INFO__;
  if (runtime?.version) {
    return {
      version: runtime.version,
      environment: runtime.environment || APP_ENVIRONMENT,
      buildTime: runtime.buildTime || APP_BUILD_TIME,
      gitCommit: runtime.gitCommit || APP_GIT_COMMIT,
    };
  }

  return {
    version: APP_VERSION,
    environment: APP_ENVIRONMENT,
    buildTime: APP_BUILD_TIME,
    gitCommit: APP_GIT_COMMIT,
  };
}

/** Compact label for footers: `v1.2.3` */
export function getAppVersionLabel(): string {
  return `v${getDisplayVersion(getAppVersionInfo().version)}`;
}

export function getVersionJsonUrl(): string {
  const publicUrl = process.env.PUBLIC_URL || "";
  const normalizedBase = publicUrl.endsWith("/") ? publicUrl : `${publicUrl}/`;
  return `${normalizedBase}version.json`;
}

/** Fetch deployed manifest (works when /version.json is served as static file). */
export async function hydrateAppVersionFromManifest(): Promise<AppVersionInfo | null> {
  if (globalThis.window === undefined) return null;

  try {
    const response = await globalThis.fetch(getVersionJsonUrl(), { cache: "no-store" });
    if (!response.ok) return null;

    const payload = await response.json();
    if (!payload?.version) return null;

    const info: AppVersionInfo = {
      version: String(payload.version),
      environment: String(payload.environment || APP_ENVIRONMENT),
      buildTime: String(payload.buildTime || APP_BUILD_TIME),
      gitCommit: String(payload.gitCommit || APP_GIT_COMMIT),
    };

    globalThis.window.__MYTARA_APP_INFO__ = info;
    return info;
  } catch {
    return null;
  }
}

/** Single-line string for support tickets and console output. */
export function getAppVersionDebugString(): string {
  const { version, environment, buildTime, gitCommit } = getAppVersionInfo();
  const parts = [`myTara v${version}`, `env=${environment}`, `commit=${gitCommit}`];
  if (buildTime) {
    parts.push(`built=${buildTime}`);
  }
  return parts.join(" | ");
}

/** Expose version on window for support scripts and browser devtools. */
export function exposeAppVersionOnWindow(): void {
  if (globalThis.window === undefined) return;
  globalThis.window.__MYTARA_APP_INFO__ = getAppVersionInfo();
}

/** Log version once at startup for debugging. */
export function logAppVersion(): void {
  if (process.env.NODE_ENV === "test") return;
  console.info(`[myTara] ${getAppVersionDebugString()}`);
}
